import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Plus, Trash2, FilePlus2 } from 'lucide-react';
import { care, fail, rm } from './careApi';
import { Btn, Card, Input } from './careUi';

const ChargeList = ({ title, list, set, testid }) => (
  <div>
    <div className="flex items-center justify-between mb-2">
      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">{title}</p>
      <button type="button" onClick={() => set([...list, { label: '', amount: 0, billed: false }])} className="text-xs text-teal-700 inline-flex items-center gap-1" data-testid={`${testid}-add`}><Plus className="w-3 h-3" />Add</button>
    </div>
    {list.map((x, i) => (
      <div key={i} className="flex gap-2 mb-2 items-center">
        <input className="flex-1 rounded-lg border border-slate-300 px-3 py-1.5 text-sm disabled:bg-slate-50" disabled={x.billed} placeholder="Description" value={x.label} data-testid={`${testid}-label-${i}`}
          onChange={(e) => set(list.map((y, j) => (j === i ? { ...y, label: e.target.value } : y)))} />
        <input className="w-28 rounded-lg border border-slate-300 px-3 py-1.5 text-sm text-right disabled:bg-slate-50" disabled={x.billed} type="number" min="0" step="0.01" value={x.amount} data-testid={`${testid}-amount-${i}`}
          onChange={(e) => set(list.map((y, j) => (j === i ? { ...y, amount: e.target.value } : y)))} />
        {x.billed && <span className="text-[10px] text-emerald-700">billed</span>}
        <button type="button" onClick={() => set(list.filter((_, j) => j !== i))}><Trash2 className="w-4 h-4 text-slate-400 hover:text-rose-600" /></button>
      </div>
    ))}
  </div>
);

export default function BillingProfile({ rid, onInvoice, tick }) {
  const [b, setB] = useState(null);
  const [period, setPeriod] = useState(new Date().toISOString().slice(0, 7));
  const [busy, setBusy] = useState('');
  useEffect(() => { care.get(`/residents/${rid}/billing`).then((r) => setB(r.data)).catch(fail); }, [rid, tick]);
  if (!b) return null;
  const n = (v) => Number(v) || 0;
  const total = n(b.accommodation_fee) + n(b.care_fee) + (b.recurring || []).reduce((s, x) => s + n(x.amount), 0) - n(b.discount);
  const set = (k) => (e) => setB({ ...b, [k]: e.target.value });

  const save = async () => {
    setBusy('save');
    try {
      const clean = (l) => (l || []).filter((x) => x.label.trim()).map((x) => ({ ...x, amount: n(x.amount) }));
      const { data } = await care.put(`/residents/${rid}/billing`, {
        ...b, accommodation_fee: n(b.accommodation_fee), care_fee: n(b.care_fee), deposit: n(b.deposit), discount: n(b.discount),
        due_days: parseInt(b.due_days, 10) || 0, recurring: clean(b.recurring), one_time: clean(b.one_time),
      });
      setB(data); toast.success('Billing profile saved');
    } catch (e) { fail(e); } finally { setBusy(''); }
  };
  const generate = async () => {
    setBusy('gen');
    try {
      const { data } = await care.post('/invoices/generate', { resident_id: rid, period });
      toast.success(`Invoice ${data.invoice_no} created`);
      const fresh = await care.get(`/residents/${rid}/billing`);
      setB(fresh.data);
      onInvoice(data);
    }
    catch (e) { fail(e); } finally { setBusy(''); }
  };

  return (
    <Card className="p-5" data-testid="billing-profile">
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Input label="Monthly accommodation fee (RM)" type="number" min="0" value={b.accommodation_fee} onChange={set('accommodation_fee')} testid="bill-accommodation" />
        <Input label="Monthly care fee (RM)" type="number" min="0" value={b.care_fee} onChange={set('care_fee')} testid="bill-care" />
        <Input label="Deposit (RM)" type="number" min="0" value={b.deposit} onChange={set('deposit')} testid="bill-deposit" disabled={b.deposit_billed} />
        <Input label="Discount / adjustment (RM)" type="number" min="0" value={b.discount} onChange={set('discount')} testid="bill-discount" />
        <Input label="Discount label" value={b.discount_label} onChange={set('discount_label')} testid="bill-discount-label" />
        <Input label="Billing start date" type="date" value={b.billing_start_date} onChange={set('billing_start_date')} testid="bill-start" />
        <Input label="Invoice due (days after invoice)" type="number" min="0" value={b.due_days} onChange={set('due_days')} testid="bill-due-days" />
      </div>
      <div className="grid md:grid-cols-2 gap-6 mt-5">
        <ChargeList title="Other recurring charges" list={b.recurring || []} set={(l) => setB({ ...b, recurring: l })} testid="bill-recurring" />
        <ChargeList title="One-time charges (billed on next invoice)" list={b.one_time || []} set={(l) => setB({ ...b, one_time: l })} testid="bill-onetime" />
      </div>
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mt-6 pt-5 border-t border-slate-100">
        <div>
          <p className="text-xs text-slate-500">Monthly total payable</p>
          <p className="text-2xl font-bold text-slate-900 tabular-nums" data-testid="bill-monthly-total">{rm(total)}</p>
          {(!b.deposit_billed && n(b.deposit) > 0) && <p className="text-xs text-slate-500">+ deposit {rm(b.deposit)} on the next invoice</p>}
        </div>
        <div className="flex flex-wrap items-end gap-2">
          <Btn variant="ghost" loading={busy === 'save'} onClick={save} data-testid="bill-save">Save billing profile</Btn>
          <Input label="Billing period" type="month" value={period} onChange={(e) => setPeriod(e.target.value)} testid="bill-period" />
          <Btn loading={busy === 'gen'} onClick={generate} data-testid="bill-generate-invoice"><FilePlus2 className="w-4 h-4" />Generate invoice</Btn>
        </div>
      </div>
    </Card>
  );
}
